import { useEffect, useState } from "react"

import { App, Flex, Modal, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"

import EmptyComponent from "../../../Components/Empty"
import InviteForm from "../../../Components/Forms/InviteForm"
import Table from "../../../Components/Table"
import { useTitleSectionAction } from "../../../Components/TitleSection/TitleSectionContext"
import { useExternalDemoMode } from "../../../hooks/useExternalDemoMode"
import { modalConfig } from "../../../theme"
import { ensureHttps } from "../../../Utils/utils"
import SettingCreators from "../../Setting/reducer"
import InviteUserButton from "./components/InviteUserButton"
import { columns } from "./Table/columns"
import { TeamPageCard } from "./TeamPageCard"

import "./styles.less"

const { Title, Text } = Typography

const Users = ({
  isMobile,
  theme,
  userInfo,
  isAdmin,
  businessAccounts,
  rolesFetching,
  roles,
  fetching,
  users,
  saveFetching,
  saveResult,
  businessAccount,
  admin_selected_business_account_id,
  isBusinessSettingsPage,
  ...props
}) => {
  const { t } = useTranslation()
  const externalDemoMode = useExternalDemoMode()
  const { setAction } = useTitleSectionAction()

  const { modal, notification } = App.useApp()
  const [open, setOpen] = useState(false)
  const [selectUser, setSelectUser] = useState(null)

  const isDemo = isAdmin ? businessAccount?.["is_demo"] : userInfo?.is_demo
  const isInviteDisabledForAdmin =
    businessAccount?.status === "incomplete" || businessAccount?.is_demo
  const isInviteDisabledForUser =
    (userInfo?.is_demo && userInfo?.ba_role?.name !== "Administrator") || externalDemoMode
  const isInviteDisabled = isAdmin ? isInviteDisabledForAdmin : isInviteDisabledForUser

  const baOnlyOwnerAdmin = businessAccounts?.filter((ba) =>
    selectUser ? true : ba?.["role"]?.id <= 2 && !ba?.["is_demo"] && ba?.can_invite
  )

  const data = users?.results || users || []

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  useEffect(() => {
    if (!roles?.length) props.getRoles()
    props.getUsers(isAdmin ? { business_account_id: admin_selected_business_account_id } : {})
  }, [])

  useEffect(() => {
    if (saveResult) {
      let dataInfo
      if (saveResult === "removed") {
        dataInfo = {
          message: t("User has been removed")
        }
      } else if (saveResult === "invite_create") {
        dataInfo = {
          message: t("Invite sent"),
          description: t("You’ve invited new user to manage your business accounts.")
        }
      } else if (saveResult === "invite_resend") {
        dataInfo = {
          message: t("Invitation has been resent")
        }
      } else if (saveResult === "invite_update") {
        dataInfo = {
          message: t("Permissions have been updated")
        }
      }

      setOpen(false)
      setSelectUser(null)
      notification.success({
        ...dataInfo,
        ...notificationConfig
      })

      props.resetResult()
    }
  }, [saveResult])

  const onSelectRow = (data, type) => {
    if (type === "resend") {
      props.resendInviteUser(data.id)
    } else if (type === "edit") {
      setSelectUser(data)
      setOpen(true)
    } else if (type === "delete") {
      modal.confirm({
        ...modalConfig("exclamation"),
        centered: !isMobile,

        title: t("Remove user"),
        content: t(
          "Are you sure you want to remove this user from managing your business accounts?"
        ),

        cancelText: t("Cancel"),
        okText: t("Remove"),
        okButtonProps: {
          ...modalConfig("exclamation")?.okButtonProps
          //danger: true,
          //ghost: true,
        },
        onOk() {
          props.removeUser(data.id)
        },
        onCancel() {
          console.log("Cancel")
        }
      })
    }
  }

  const handleCloseModal = () => {
    setOpen(false)
    setSelectUser(null)
  }

  useEffect(() => {
    if (isAdmin) return
    setAction(
      <InviteUserButton
        isMobile={isMobile}
        isDisabled={isInviteDisabled}
        onOpen={() => setOpen(true)}
      />
    )
    return () => setAction(null)
  }, [isMobile, isAdmin, userInfo, externalDemoMode])

  const handleSubmitForm = (data) => {
    const business_account_id = isAdmin ? admin_selected_business_account_id : null
    if (selectUser) {
      props.updateInviteUser({
        id: selectUser?.id,
        role_id: data?.role_id,
        business_account_id
      })
    } else {
      props.createInviteUser({
        ...data,
        business_account_id,
        ...(typeof data?.business_accounts_ids === "string"
          ? { business_accounts_ids: [data?.business_accounts_ids] }
          : { business_accounts_ids: data?.business_accounts_ids })
      })
    }
  }

  return (
    <div>
      {isAdmin && (
        <Flex style={{ marginBottom: "24px" }} justify="space-between" align="center">
          <Flex vertical={true}>
            {isBusinessSettingsPage ? (
              <>
                <Title level={4}>{`Users management for ${businessAccount?.name}`}</Title>
                <a
                  href={ensureHttps(businessAccount?.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {businessAccount?.url}
                </a>
              </>
            ) : (
              <>
                <Title level={4}>{t("Users management")}</Title>
                <Text type="secondary">
                  {t("Manage list of users which business account they can access.")}
                </Text>
              </>
            )}
          </Flex>
          <InviteUserButton
            isMobile={false}
            isDisabled={isInviteDisabled}
            onOpen={() => setOpen(true)}
          />
        </Flex>
      )}
      {!isMobile ? (
        <Table
          columns={columns({
            onSelectRow,
            t,
            theme,
            data: data,
            isAdmin,
            externalDemoMode
          })}
          loading={fetching || saveFetching}
          dataSource={data}
          expandable={{ defaultExpandAllRows: true }}
          withOutPagination={true}
        />
      ) : (
        <Spin spinning={fetching || saveFetching}>
          {!fetching && data.length === 0 ? (
            <EmptyComponent type="no_data_available" />
          ) : (
            <Flex vertical gap={8}>
              {data.map((record) => (
                <TeamPageCard
                  key={record.id}
                  record={record}
                  onSelectRow={onSelectRow}
                  isAdmin={isAdmin}
                  externalDemoMode={externalDemoMode}
                />
              ))}
            </Flex>
          )}
        </Spin>
      )}
      <Modal
        open={open}
        centered={!isMobile}
        width={isMobile ? "100%" : 442}
        title={selectUser ? t("Edit user permissions") : t("Invite user")}
        footer={null}
        closable="true"
        onCancel={handleCloseModal}
        destroyOnClose
        classNames={{ content: "clear-bg" }}
        {...(isMobile
          ? {
              transitionName: "",
              style: {
                top: "auto",
                margin: 0,
                paddingBottom: 0,
                maxWidth: "100%"
              },
              styles: { content: { borderRadius: "16px 16px 0 0" } }
            }
          : {})}
      >
        <Spin size="large" spinning={saveFetching}>
          <InviteForm
            businessAccountList={baOnlyOwnerAdmin}
            roles={roles}
            initialValues={selectUser}
            isBusinessSettingsPage={isBusinessSettingsPage}
            businessAccount={businessAccount}
            onSubmit={handleSubmitForm}
            onClose={handleCloseModal}
            isMobile={isMobile}
          />
        </Spin>
      </Modal>
    </div>
  )
}

const mapStateToProps = (state) => ({
  isMobile: state.nav.isMobile,
  theme: state.nav.theme,
  userInfo: state.auth.userInfo,
  isAdmin: state.auth.isAdmin,
  businessAccounts: state.user.businessAccountsWithRole,

  rolesFetching: state.setting.rolesFetching,
  roles: state.setting.roles,
  fetching: state.setting.fetching,
  users: state.setting.users,
  saveFetching: state.setting.saveFetching,
  saveResult: state.setting.saveResult,
  businessAccount: state.user.businessAccount,
  admin_selected_business_account_id: state.user.admin_selected_business_account_id
})

const mapDispatchToProps = (dispatch) => ({
  getRoles: () => dispatch(SettingCreators.getUsersManagementRolesRequest()),
  getUsers: (data) => dispatch(SettingCreators.getUsersManagementRequest(data)),

  createInviteUser: (data) => dispatch(SettingCreators.createInviteUserRequest(data)),
  updateInviteUser: (data) => dispatch(SettingCreators.updateInviteUserRequest(data)),
  resendInviteUser: (userId) => dispatch(SettingCreators.resendInviteUserRequest(userId)),
  removeUser: (userId) => dispatch(SettingCreators.removeUserRequest(userId)),
  resetResult: () => dispatch(SettingCreators.settingsSaveResultReset())
})

export default connect(mapStateToProps, mapDispatchToProps)(Users)
